import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME, USE_LIST_PLAYERS_BY_TEAM_ID, USE_RETRIEVE_TEAMS_BY_PLAYER_ID, } from "../ApiProvider";
import { api } from "./api";
import { USE_RETRIEVE_PLAYER_FEED, USE_RETRIEVE_TEAM_FEED } from "./queryKeys";
const USE_LIST_MY_INVITATIONS = "useListMyInvitations";
const USE_LIST_TEAM_INVITATIONS = "useListTeamInvitations";
const USE_RETRIEVE_INVITATION = "useRetrieveInvitation";
const USE_RETRIEVE_TEAM_INVITE_LINK = "useRetrieveTeamInviteLink";
const USE_PREVIEW_INVITE_LINK = "usePreviewInviteLink";
const USE_PREVIEW_INVITATION_BY_TOKEN = "usePreviewInvitationByToken";
const USE_SEARCH_INVITABLE_PLAYERS = "useSearchInvitablePlayers";
// League-team invitation query keys
const USE_LIST_MY_LEAGUE_TEAM_INVITATIONS = "useListMyLeagueTeamInvitations";
const USE_LIST_LEAGUE_INVITATIONS = "useListLeagueInvitations";
const USE_RETRIEVE_LEAGUE_TEAM_INVITATION = "useRetrieveLeagueTeamInvitation";
const USE_SEARCH_INVITABLE_TEAMS = "useSearchInvitableTeams";
// Team ownership invitation query keys
const USE_LIST_MY_OWNERSHIP_INVITATIONS = "useListMyOwnershipInvitations";
const USE_LIST_TEAM_OWNERSHIP_INVITATIONS = "useListTeamOwnershipInvitations";
const USE_RETRIEVE_OWNERSHIP_INVITATION = "useRetrieveOwnershipInvitation";
const USE_PREVIEW_OWNERSHIP_BY_TOKEN = "usePreviewOwnershipByToken";
const USE_LIST_MERGEABLE_TEAMS = "useListMergeableTeams";
const USE_SEARCH_OWNERSHIP_INVITABLE_USERS = "useSearchOwnershipInvitableUsers";
export const useRetrieveTeamInviteLink = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_TEAM_INVITE_LINK, teamId],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/invite-link/`);
            return response.data;
        },
        enabled: !!teamId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        retry: false,
    });
};
export const useListMyInvitations = () => {
    return useQuery({
        queryKey: [USE_LIST_MY_INVITATIONS],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-player/my-invitations/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveInvitation = ({ id }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_INVITATION, id],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-player/${id}/`);
            return response.data;
        },
        enabled: !!id,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListTeamInvitations = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_TEAM_INVITATIONS, teamId],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/list-invitations/`);
            return response.data;
        },
        enabled: !!teamId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreateTeamPlayerInvitation = ({ teamId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.post(`/teams/${teamId}/invitations/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_TEAM_INVITATIONS, teamId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useProcessInvitation = ({ invitation, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.put(`/invitations/team-player/process/${invitation.id}/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_MY_INVITATIONS]);
            queryClient.invalidateQueries([USE_RETRIEVE_INVITATION, invitation.id]);
            queryClient.invalidateQueries([
                USE_LIST_PLAYERS_BY_TEAM_ID,
                invitation.team,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, invitation.team]);
            queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED]);
            onSuccess(data);
        },
        onError,
    });
};
export const useAcceptInvitationByToken = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ token, status, }) => {
            return api.put(`/invitations/team-player/accept-by-token/${token}/`, { status });
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_MY_INVITATIONS]);
            queryClient.invalidateQueries([
                USE_LIST_PLAYERS_BY_TEAM_ID,
                data.data.team,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, data.data.team]);
            queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED]);
            onSuccess(data);
        },
        onError,
    });
};
export const usePreviewInvitationByToken = ({ token, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_PREVIEW_INVITATION_BY_TOKEN, token],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-player/accept-by-token/${token}/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!token,
        retry: false,
    });
};
export const useCancelInvitation = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ invitationId, }) => {
            return api.delete(`/invitations/team-player/cancel/${invitationId}/`);
        },
        onMutate,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries([
                USE_LIST_TEAM_INVITATIONS,
                variables.teamId,
            ]);
            onSuccess();
        },
        onError,
    });
};
export const useGenerateInviteLink = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.post(`/teams/${teamId}/invite-link/generate/`);
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_INVITE_LINK, teamId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useRevokeInviteLink = ({ teamId, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.post(`/teams/${teamId}/invite-link/revoke/`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_INVITE_LINK, teamId]);
            onSuccess();
        },
        onError,
    });
};
export const useJoinTeamByLink = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (token) => {
            return api.put(`/invitations/team-links/${token}/join/`);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([
                USE_LIST_PLAYERS_BY_TEAM_ID,
                data.data.team,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAM_FEED, data.data.team]);
            queryClient.invalidateQueries([USE_RETRIEVE_PLAYER_FEED]);
            onSuccess(data);
        },
        onError,
    });
};
export const usePreviewInviteLink = ({ token, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_PREVIEW_INVITE_LINK, token],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-links/${token}/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!token,
    });
};
export const useSearchInvitablePlayers = ({ teamId, searchTerm, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_INVITABLE_PLAYERS, teamId, searchTerm],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/players/invitable/`, { params: { ...(searchTerm ? { q: searchTerm } : {}) } });
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!teamId,
    });
};
export const useSearchOwnershipInvitableUsers = ({ teamId, searchTerm, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_OWNERSHIP_INVITABLE_USERS, teamId, searchTerm],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/users/ownership-invitable/`, { params: { ...(searchTerm ? { q: searchTerm } : {}) } });
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!teamId,
    });
};
// ──────────────────────────────────────────────
// League-Team Invitation hooks
// ──────────────────────────────────────────────
export const useListMyLeagueTeamInvitations = () => {
    return useQuery({
        queryKey: [USE_LIST_MY_LEAGUE_TEAM_INVITATIONS],
        queryFn: async () => {
            const response = await api.get(`/invitations/league-team/my-invitations/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveLeagueTeamInvitation = ({ id, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_LEAGUE_TEAM_INVITATION, id],
        queryFn: async () => {
            const response = await api.get(`/invitations/league-team/${id}/`);
            return response.data;
        },
        enabled: !!id,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListLeagueInvitations = ({ leagueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_LEAGUE_INVITATIONS, leagueId],
        queryFn: async () => {
            const response = await api.get(`/leagues/${leagueId}/invitations/list/`);
            return response.data;
        },
        enabled: !!leagueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useSearchInvitableTeams = ({ leagueId, searchTerm, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_SEARCH_INVITABLE_TEAMS, leagueId, searchTerm],
        queryFn: async () => {
            const response = await api.get(`/leagues/${leagueId}/teams/invitable/`, {
                params: { ...(searchTerm ? { q: searchTerm } : {}) },
            });
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!leagueId,
    });
};
export const useCreateLeagueTeamInvitation = ({ leagueId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.post(`/leagues/${leagueId}/invitations/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_LEAGUE_INVITATIONS, leagueId]);
            onSuccess(data);
        },
        onError,
    });
};
export const useProcessLeagueTeamInvitation = ({ invitation, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.put(`/invitations/league-team/process/${invitation.id}/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_MY_LEAGUE_TEAM_INVITATIONS]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_LEAGUE_TEAM_INVITATION,
                invitation.id,
            ]);
            queryClient.invalidateQueries([
                USE_LIST_LEAGUE_INVITATIONS,
                invitation.league,
            ]);
            onSuccess(data);
        },
        onError,
    });
};
export const useCancelLeagueTeamInvitation = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ invitationId, }) => {
            return api.delete(`/invitations/league-team/cancel/${invitationId}/`);
        },
        onMutate,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries([
                USE_LIST_LEAGUE_INVITATIONS,
                variables.leagueId,
            ]);
            onSuccess();
        },
        onError,
    });
};
// ──────────────────────────────────────────────
// Team Ownership Invitation hooks
// ──────────────────────────────────────────────
export const useListMyOwnershipInvitations = () => {
    return useQuery({
        queryKey: [USE_LIST_MY_OWNERSHIP_INVITATIONS],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-ownership/my-invitations/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveOwnershipInvitation = ({ id, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_OWNERSHIP_INVITATION, id],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-ownership/${id}/`);
            return response.data;
        },
        enabled: !!id,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListTeamOwnershipInvitations = ({ teamId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_TEAM_OWNERSHIP_INVITATIONS, teamId],
        queryFn: async () => {
            const response = await api.get(`/teams/${teamId}/ownership-invitations/list/`);
            return response.data;
        },
        enabled: !!teamId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useListMergeableTeams = ({ invitationId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_LIST_MERGEABLE_TEAMS, invitationId],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-ownership/${invitationId}/mergeable-teams/`);
            return response.data;
        },
        enabled: !!invitationId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreateOwnershipInvitation = ({ teamId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.post(`/teams/${teamId}/ownership-invitations/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([
                USE_LIST_TEAM_OWNERSHIP_INVITATIONS,
                teamId,
            ]);
            onSuccess(data);
        },
        onError,
    });
};
export const useProcessOwnershipInvitation = ({ invitation, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return api.put(`/invitations/team-ownership/process/${invitation.id}/`, payload);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_MY_OWNERSHIP_INVITATIONS]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_OWNERSHIP_INVITATION,
                invitation.id,
            ]);
            queryClient.invalidateQueries([
                USE_LIST_TEAM_OWNERSHIP_INVITATIONS,
                invitation.team,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess(data);
        },
        onError,
    });
};
export const usePreviewOwnershipByToken = ({ token, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_PREVIEW_OWNERSHIP_BY_TOKEN, token],
        queryFn: async () => {
            const response = await api.get(`/invitations/team-ownership/accept-by-token/${token}/`);
            return response.data;
        },
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
        enabled: enabled && !!token,
        retry: false,
    });
};
export const useAcceptOwnershipByToken = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ token, status, mergeIntoTeamId, }) => {
            return api.put(`/invitations/team-ownership/accept-by-token/${token}/`, { status, ...(mergeIntoTeamId ? { mergeIntoTeamId } : {}) });
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_LIST_MY_OWNERSHIP_INVITATIONS]);
            queryClient.invalidateQueries([USE_RETRIEVE_TEAMS_BY_PLAYER_ID]);
            onSuccess(data);
        },
        onError,
    });
};
export const useCancelOwnershipInvitation = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ invitationId, }) => {
            return api.delete(`/invitations/team-ownership/cancel/${invitationId}/`);
        },
        onMutate,
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries([
                USE_LIST_TEAM_OWNERSHIP_INVITATIONS,
                variables.teamId,
            ]);
            onSuccess();
        },
        onError,
    });
};
